import React, {useState} from "react";

import PropTypes from "prop-types";
import {useCubeQuery} from "@cubejs-client/react";
import {Button, Col, Modal, Row, Statistic, Table, Typography} from "antd";
import {
    Area,
    AreaChart,
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Legend,
    Line,
    LineChart,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from "recharts";

import "../recharts-theme.less";
import moment from "moment";
import numeral from "numeral";
import MocHelper from "../../utils/MocHelper";
import RussianMap from "../../Modules/src/russianMap"
import {colors5 as colors} from "../Common/Colors"
import {inject, observer} from "mobx-react";
import {isNumber} from "underscore";
import {Spinner} from "../Spinner";
import {useVoc, decodeVoc, dataset, decodeDataset} from "../../hooks/voc.hook";

import mapValues from 'object.map';
import {RegionMap} from "../RegionMap/RegionMap";

const numberFormatter = item => numeral(item).format("0,0.[0]").replaceAll(/,/g,' ').replaceAll(/\./g, ',');
//const dateFormatter = item => item;
const dateFormatter = item => moment(item).format("DD MMM YY");

// const colors = ["#7DB3FF", "#49457B", "#FF7C78"];


const xAxisFormatter = (item) => {
  if (moment(item).isValid()) {
    return dateFormatter(item)
  } else {
    return item;
  }
}

// const xAxisFormatter = (item) => (item)

const CartesianChart = ({ resultSet, children, ChartComponent, height, layout }) => {
    return (
    <ResponsiveContainer width="100%" height={height}>
        <ChartComponent margin={{ left: 20 }} data={resultSet.chartPivot()} layout={layout ? layout : "horizontal"}>
            <XAxis axisLine={false} tickLine={false} tickFormatter={xAxisFormatter} dataKey="x" minTickGap={20} />
            <YAxis axisLine={false} tickLine={false} tickFormatter={numberFormatter} />
            <CartesianGrid vertical={false} />
            { children }
            { resultSet.seriesNames().length > 1 ? <Legend /> : <></>}
            <Tooltip labelFormatter={xAxisFormatter} formatter={numberFormatter} />
        </ChartComponent>
    </ResponsiveContainer>
)}

function renderRg(value, entry)  {
    return <span>{value ? value.replace(/^[0-9\s]*\-\s/, '')  : ''}</span>;
}

const TypeToChartComponent = {
    line: ({ resultSet, height, handleDrillClick, vizState  }) => {
        return (
        <CartesianChart resultSet={resultSet} height={height} ChartComponent={LineChart}  data={decodeDataset(resultSet.chartPivot(vizState.pivotConfig))}>
            {resultSet.seriesNames().map((series, i) => (
                <Line
                    key={series.key}
                    stackId="a"
                    dataKey={series.key}
                    name={renderRg(series.title)}
                    stroke={colors[i]}
                    type="monotone"
                    onClick={event => handleDrillClick(event, series.yValues)}
                />
            ))}
        </CartesianChart>
    )},
    bar: ({ resultSet, height, handleDrillClick, vizState  }) => (
        <CartesianChart
            resultSet={resultSet}
            height={height}
            ChartComponent={BarChart}
            // data={resultSet.chartPivot()}
            data={decodeDataset(resultSet.chartPivot())}
        >
            <XAxis dataKey="x" tickFormatter={dateFormatter} />
            <YAxis />
            {resultSet.seriesNames().map((series, i) => (
                <Bar
                    key={series.key}
                    stackId="a"
                    dataKey={series.key}
                    name={renderRg(series.title)}
                    fill={colors[i]}
                    // data={decodeDataset(resultSet.chartPivot(vizState))}
                    onClick={event => handleDrillClick(event, series.yValues)}
                />
            ))}
        </CartesianChart>
    ),
    hbar: ({ resultSet, height, handleDrillClick, vizState  }) => (
        <CartesianChart
            layout="vertical"
            resultSet={resultSet}
            height={height}
            ChartComponent={BarChart}
            // data={resultSet.chartPivot()}>
            data={decodeDataset(resultSet.chartPivot(vizState.pivotConfig))}>
            <XAxis dataKey="x" tickFormatter={dateFormatter} />
            <YAxis />
            {resultSet.seriesNames().map((series, i) => (
                <Bar
                    key={series.key}
                    stackId="a"
                    dataKey={series.key}
                    name={renderRg(series.title)}
                    fill={colors[i]}

                    // data={decodeDataset(resultSet.chartPivot(vizState.pivotConfig))}

                    onClick={event => handleDrillClick(event, series.yValues)}
                />
            ))}
        </CartesianChart>),
        area: ({ resultSet, height, handleDrillClick, vizState  }) => (
        <CartesianChart
            type={"monotone"}
            resultSet={resultSet}
            height={height} ChartComponent={AreaChart} data={decodeDataset(resultSet.chartPivot(vizState.pivotConfig))} >
            {resultSet.seriesNames().map((series, i) => (
                <Area
                    key={series.key}
                    stackId="a"
                    dataKey={series.key}
                    name={renderRg(series.title)}
                    stroke={colors[i]}
                    fill={colors[i]}
                    // type="monotone"
                    onClick={event => handleDrillClick(event, series.yValues)}
                    // data={decodeDataset(resultSet.chartPivot(vizState.pivotConfig))}
                    // data={resultSet.chartPivot(vizState.pivotConfig)}
                    // data={decodeDataset(resultSet.chartPivot(vizState.pivotConfig))}
                />
            ))}
        </CartesianChart>
    ),
    pie: ({ resultSet, height, handleDrillClick, vizState }) => (
        <ResponsiveContainer width="100%" height={height}>
            <PieChart>
                <Pie
                    isAnimationActive={false}
                    data={resultSet.chartPivot(vizState.pivotConfig)}
                    nameKey="x"
                    dataKey={resultSet.seriesNames()[0].key}
                    fill="#8884d8"
                >
                    {decodeDataset(resultSet.chartPivot().map((e, index) => {
                        return (
                            <Cell key={index} fill={colors[index % colors.length]}
                                  onClick={event => handleDrillClick({xValues: e.xValues}, e.yValues)}/>
                        )}))}
                </Pie>
                <Legend formatter={renderRg} />
                <Tooltip />
            </PieChart>
        </ResponsiveContainer>
    ),
    table: ({ resultSet, handleModalClick, vizState }) => (
        <Table

            columns={[...resultSet
                .tableColumns(vizState.pivotConfig)
                .map((c, i) =>
                    (
                        { ...c,
                            dataIndex: c.key,
                            sorter: (a, b) => a[c.key] == b[c.key]? 0: ( a[c.key] > b[c.key] ? 1: -1),
                            align: i > 0 ? "right" : "left",
                            render: (item) => ((c.title || '').indexOf('%') > 0 ?
                                (isNumber(item) ? numeral(item).format('0.0') : item) :
                                (isNumber(item) ? numeral(item).format('0,0').replaceAll(/,/g, ' ') : renderRg(item))),

                            })),
                {
                    dataIndex: resultSet.tableColumns()[0].key,
                    title: "Детали",
                    render:
                        item => <Button
                            title={"Детали"}
                            type={"primary"}
                            disabled
                            onClick={handleModalClick(item)}>Детали</Button>}
            ]
            }
            dataSource={decodeDataset(resultSet.tablePivot(vizState.pivotConfig))}
            rowKey={'renderCode'}
        />
    ),
    number: ({ resultSet }) => (
        <Row
            type="flex"
            justify="center"
            align="middle"
            style={{
                height: "100%"
            }}
        >
            <Col>
                {resultSet.seriesNames().map(s => (
                    <Statistic
                        value={resultSet.totalRow()[s.key]}
                        formatter={(item) =>
                            <Typography.Title level={4}>
                                {numeral(item).format('0,0.[0]')
                                .replaceAll(/,/g,' ')
                                .replaceAll(/\./g,',')
                            }
                            </Typography.Title>} />
                ))}
            </Col>
        </Row>
    ),
    map: ({resultSet, handleDrillClick, vizState, rootFilter}) => {
        const filter = rootFilter.items.filter(d => d.dimension.indexOf('areaCd') >= 0)
        const selectedRegion = filter && filter[0] && filter[0].value
        return (
            <RegionMap
                data={resultSet.chartPivot(vizState.pivotConfig)}
                dataKey={resultSet.seriesNames()[0].key}
                pal={vizState.ext && vizState.ext.pal}
            />

          /*  <RussianMap
                data={resultSet.chartPivot()}
                dataKey={resultSet.seriesNames()[0].key}
                handleDrillClick={handleDrillClick}
                pal={vizState.ext && vizState.ext.pal}
                selectedRegion={selectedRegion}

            />*/
        )}
}

const TypeToMemoChartComponent = Object.keys(TypeToChartComponent)
    .map(key => ({
        [key]: React.memo(TypeToChartComponent[key])
    }))
    .reduce((a, b) => ({ ...a, ...b }));

function PateintTableView(drillDownResponse, drillDownData, setPatientCD, setOpenTL) {
    return () => {
        if (drillDownResponse.isLoading) {
            return ("Loading...")
        } else {

            const temp = drillDownData()
            const ready = temp.map((k) => ({
                family: k["Patients.family"],
                name: k["Patients.name"],
                id: k["Patients.id"],
                policysdate: MocHelper.quickDateConvert(k["Patients.policysdate"]),
                birthsdate: MocHelper.quickDateConvert(k["Patients.birthsdate"]),
                deathsdate: MocHelper.quickDateConvert(k["Patients.deathsdate"]),
                regsdate: MocHelper.quickDateConvert(k["Patients.regsdate"]),
                unregsdate: MocHelper.quickDateConvert(k["Patients.unregsdate"]),
                cd: k["Patients.cdPatient"]

            }))
            const columns = [
                {
                    title: 'Фамилия',
                    dataIndex: 'family',
                    key: 'family',
                },
                {
                    title: 'Имя',
                    dataIndex: 'name',
                    key: 'name',
                },
                {
                    title: 'ИД',
                    dataIndex: 'cdPatient',
                    key: 'deathsdate',
                    render: (text, record) => <div>{(record.id || record.cd)}</div>
                },
                {
                    title: 'Дата рождения',
                    dataIndex: 'birthsdate',
                    key: 'birthsdate',
                },/*
                  {
                    title: 'Дата смерти',
                    dataIndex: 'deathsdate',
                    key: 'deathsdate',
                  },*/
                {
                    title: 'Детали',
                    dataIndex: 'cdPatient',
                    key: 'cdPatient',
                    render: (text, record) => (
                        <>
                            <Button type="primary"  onClick={
                                () => {
                                    // console.log(record)
                                    setPatientCD(record.cd)
                                    // setOpen(false);
                                    setOpenTL(true)
                                }
                            }> Детали </Button>

                        </>
                    ),
                },
            ]
            return (
                <Table
                    columns={columns}
                    dataSource={ready}
                >
                </Table>)
        }
    };
}

const PatientViewDrillDown1 = ({visible, onOk, drillDownResponse, drillDownData}) => {
    const [openTL, setOpenTL] = React.useState(false);
    const [patientCd, setPatientCD]  = React.useState(false);
    const handleCloseTL = () => {setOpenTL(false)}


    return <>
        <Modal
            visible={visible}
            onOk={onOk}
            width={1000}
        >
            {
                PateintTableView(drillDownResponse, drillDownData, setPatientCD, setOpenTL)()
            }
        </Modal>
    </>;
}

PatientViewDrillDown1.propTypes = {
    visible: PropTypes.bool,
    onOk: PropTypes.func,
    drillDownResponse: PropTypes.any,
    drillDownData: PropTypes.func,
};

const renderChart = Component => ({ resultSet, error, height, vizState, ddView, rootFilter, readyQuery }) => {
    const [drillDownQuery, setDrillDownQuery] = useState();
    const [open, setOpen] = React.useState(false);

    const handleModalClick = (event, yValues) => {
    }

    const handleSetFilterClick = (event, yValues) => {

        const dims = vizState && vizState.query && vizState.query.dimensions
        if (dims)
        {
            for (let i = 0; i < dims.length; i++) {
                rootFilter.setFilter({dimension: dims[i], value: event.xValues && event.xValues[i]})
            }
        }
    };
    const handleDrillClick = (event, yValues) => {
      console.log("event-", event)
      console.log("yValues-", yValues)
      if (event.xValues != null) {
          const drillDownQueryNew = resultSet.drillDown(
              {
                  xValues: event.xValues,
                  yValues
              })
          if (drillDownQueryNew && readyQuery.filters.length >= 1) {
              drillDownQueryNew.filters = drillDownQueryNew.filters.concat(readyQuery.filters)
          }
          if (drillDownQueryNew && readyQuery.segments?.length >= 1) {
              drillDownQueryNew.segments = (drillDownQueryNew.segments || []).concat(readyQuery.segments)
          }
          setDrillDownQuery(drillDownQueryNew);
      }
      setOpen(true);
    };

    const drillDownResponse = useCubeQuery(
        drillDownQuery,
        {
            skip: !drillDownQuery
        }
    );
    const drillDownData = () => (drillDownResponse.resultSet && drillDownResponse.resultSet.tablePivot()) || []

    const handleClose = () => {setOpen(false)}

    const chart = () => (resultSet && <Component vizState={vizState}
                                                 height={height} resultSet={resultSet}
                                                 handleDrillClick={handleDrillClick} handleModalClick={handleModalClick}
                                                 rootFilter={rootFilter}/>) ||
        (error && error.toString()) || <Spinner/>
    return(
      <>
          {chart()}
          <PatientViewDrillDown1  visible={open}
                                 onOk={handleClose} drillDownResponse={drillDownResponse} drillDownData={drillDownData}/>
      </>
  )
};

const ChartRenderer = inject('rootFilter')(observer(({ vizState, chartHeight, ddView, rootFilter }) => {
    const { query, chartType } = vizState;
    const component = TypeToMemoChartComponent[chartType];
    const {filters, ...newQuery} = query
    const readyQuery = {...newQuery, filters: rootFilter.appendFilter(filters, query.dimensions)}
    const renderProps = useCubeQuery(readyQuery);
    return component && renderChart(component)({ height: chartHeight, ...renderProps, vizState, ddView, rootFilter, readyQuery });
}));

ChartRenderer.propTypes = {
    vizState: PropTypes.object,
    cubejsApi: PropTypes.object
};
ChartRenderer.defaultProps = {
    vizState: {},
    chartHeight: 300,
    cubejsApi: null
};
export default ChartRenderer;
